import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { exportCardToPDF } from '@/utils/cardPdf';
import { useAuth } from '@/contexts/AuthContext';

function stripLen(html) {
  if (!html) return 0;
  const d = document.createElement('div'); d.innerHTML = html;
  return (d.textContent || '').trim().length;
}

export default function ExportPdfDialog({ open, onOpenChange, card, tasks = [] }) {
  const { user } = useAuth();
  const [sections, setSections] = useState({ summary: true, notes: true, decisions: true, tasks: true, transcript: false });
  const [exporting, setExporting] = useState(false);

  const hasNotes = stripLen(card?.summary_note) > 0;
  const rows = [
    { key: 'summary', label: 'Summary', count: card?.summary_bullets?.length || 0 },
    ...(hasNotes ? [{ key: 'notes', label: 'Notes', count: 1, isNotes: true }] : []),
    { key: 'decisions', label: 'Decisions', count: card?.decisions?.length || 0 },
    { key: 'tasks', label: 'Tasks', count: tasks?.length || 0 },
    ...(card?.transcript ? [{ key: 'transcript', label: 'Full transcript', count: 1, isTranscript: true }] : []),
  ];

  const run = async () => {
    if (!Object.values(sections).some(Boolean)) {
      toast.error('Select at least one section to include');
      return;
    }
    setExporting(true);
    try {
      await exportCardToPDF(card, tasks, sections, { preparedBy: user?.name || '' });
      onOpenChange(false);
    } catch {
      toast.error('Could not generate the PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!exporting) onOpenChange(o); }}>
      <DialogContent data-testid="export-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Export PDF</DialogTitle>
          <DialogDescription>Choose which sections to include in the export.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 py-1">
          {rows.map(({ key, label, count, isTranscript, isNotes }) => (
            <label
              key={key}
              data-testid={`export-section-${key}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${sections[key] ? 'border-[#6994c6] bg-[#6994c6]/5' : 'border-border hover:bg-secondary/40'} ${count === 0 ? 'opacity-50' : ''}`}
            >
              <Checkbox
                checked={sections[key]}
                disabled={count === 0}
                onCheckedChange={(v) => setSections(s => ({ ...s, [key]: !!v }))}
                data-testid={`export-section-checkbox-${key}`}
              />
              <span className="flex-1 text-sm font-medium text-foreground">{label}</span>
              <span className="text-xs text-muted-foreground">{(isTranscript || isNotes) ? (count ? 'available' : 'none') : `${count} item${count === 1 ? '' : 's'}`}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            disabled={exporting}
            data-testid="export-pdf-cancel"
            className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={run}
            disabled={exporting}
            data-testid="export-pdf-confirm"
            className="inline-flex items-center justify-center gap-2 text-sm font-semibold h-10 px-4 rounded-xl text-white transition-colors disabled:opacity-50"
            style={{ background: '#6994c6' }}
          >
            {exporting ? <><Loader2 size={14} className="animate-spin" />Exporting…</> : <><Download size={14} />Export PDF</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
